import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IUserSessionData, UserType } from 'app/store/models/user.model';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { AccountService } from 'app/store/services/account.service';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { SessionService } from '../../store/services/session.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { IReducers } from '../../store/reducers';
import { openSnackbar } from '../../store/actions/page.action';
import { ButtonDisablerService } from '../../store/services/button-disabler.service';

@Component({
  selector: 'app-create-account',
  templateUrl: './create-account.component.html',
  styleUrls: ['./create-account.component.scss'],
})
export class CreateAccountComponent implements OnInit, OnDestroy {
  userSession: IUserSessionData;

  formGroup: FormGroup;

  emailTakenError: boolean;

  createButton: Button = {
    color: ButtonColor.BLUE,
    text: 'Create',
    size: ButtonSize.SMALL,
    onClick: () => this.submitAccount(),
    style: 'outline',
  };

  componentReady: boolean;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private accountService: AccountService,
    private router: Router,
    private readonly sessionService: SessionService,
    private readonly store$: Store<IReducers>,
    private readonly buttonDisablerService: ButtonDisablerService,
  ) {
  }

  get email() {
    return this.formGroup.get('email');
  }

  get firstName() {
    return this.formGroup.get('first_name');
  }

  get last_name() {
    return this.formGroup.get('last_name');
  }

  get company() {
    return this.formGroup.get('company');
  }

  get user_type() {
    return this.formGroup.get('user_type');
  }

  submitAccount() {
    if (this.formGroup.valid) {
      this.accountService
        .createAccount(
          this.email.value,
          this.firstName.value,
          this.last_name.value,
          this.company.value,
          this.user_type.value,
        )
        .subscribe(
          () => {
            this.store$.dispatch(openSnackbar({
              snackbarData: {
                data: {
                  message: 'Account successfully created. Instructions how to login and change the password are sent to user\'s email address',
                  type: 'success',
                },
              },
            }));

            if (this.userSession.user_type === UserType.MANAGER) {
              this.router.navigate(['/accounts', 'view_representatives']);
              return;
            }
            this.router.navigate(['/accounts']);
          },
          error => this.handleError(error),
        );
    } else {
      this.formGroup.markAllAsTouched();
      this.buttonDisablerService.disableActive();
    }
  }

  handleError(error: HttpErrorResponse) {
    this.store$.dispatch(openSnackbar({
      snackbarData: {
        data: {
          message: error.error,
          type: 'error',
        },
      },
    }));
    if (error.error.code === 1002) {
      this.emailTakenError = true;
    }
  }

  ngOnInit() {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe((userSession: IUserSessionData) => {
        if (userSession) {
          this.userSession = userSession;

          this.formGroup = new FormGroup({
            email: new FormControl('', [Validators.required, Validators.email]),
            first_name: new FormControl('', [Validators.required]),
            last_name: new FormControl(''),
            company: new FormControl('', [Validators.required]),
            user_type: new FormControl(UserType.SINGLE_USER, [
              Validators.required,
            ]),
          });
          this.componentReady = true;
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }
}
